import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { ApiMessage } from '@/services/apiMessage/apiMessage.types';
import * as React from 'react';
import { ManageApiMessageForm } from '../ManageApiMessageForm';
import { ManageApiMessageFormContainer } from '../ManageApiMessageFormContainer';
import { getColumns } from './Column';

export const ApiLogsTable = ({
  data,
  isLoading = false,
  pagination,
}: {
  data: ApiMessage[];
  isLoading: boolean;
  pagination: Pagination;
}) => {
  const { onOpen } = useDialogFormStore();
  const { t } = useDynamicLocale();
  const canAddNew = useCan({ allOf: [PERMS.api.message.add] });

  const openDialog = React.useCallback(
    (component: React.ReactNode, viewOnly = false) => {
      onOpen({
        modalTitle: 'logsManagement.page.apilogs.form.title',
        title: 'Manage Api Message',
        okText: t('configurationManagement.charges.manageChargeSlabModal.submit'),
        okButtonDisabled: viewOnly,
        size: 'lg',
        cancelText: t('common.page.modal.cancel'),
        component,
      });
    },
    [onOpen, t],
  );

  const onAddNew = () => openDialog(<ManageApiMessageForm />);

  const onView = React.useCallback(
    (apiMessageId: string) => openDialog(<ManageApiMessageFormContainer apiMessageId={apiMessageId} viewOnly />, true),
    [openDialog],
  );

  const onEdit = React.useCallback(
    (apiMessageId: string) => openDialog(<ManageApiMessageFormContainer apiMessageId={apiMessageId} />),
    [openDialog],
  );

  const columns = React.useMemo(() => getColumns(t, onView, onEdit), [t, onView, onEdit]);

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      columnVisibility: { createdDate: false },
    },
    meta: {
      title: (
        <Translation
          id="logsManagement.page.apilogs.form.apiMessageResponseLogs"
          defaultMessage="API Message Response Logs"
        />
      ),
      actionKey: canAddNew ? 'onAddNew' : '',
      action: canAddNew ? onAddNew : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => originalRow.apiMessageId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <div>
      <DataTable table={table} className="rounded-lg">
        <DataTableToolbar defaultDatePreset="today" />
      </DataTable>
    </div>
  );
};
